"use client";

import { useState, useCallback } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Layers,
  Copy,
  Trash2,
  ChevronsUpDown,
  Check,
  Plus,
  Minus,
  AlignCenterHorizontal,
  AlignCenterVertical,
  Maximize,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { FONT_FAMILIES } from "./ContextualToolbar";
import type { OutputField, FormField, TableCellValue, TableCellRef, VisibilityConditions, ConditionOperator, ConditionLogic } from "@/types";

const cellConditionOperatorLabels: Record<ConditionOperator, string> = {
  equals: "=",
  not_equals: "!=",
  greater_than: ">",
  greater_than_or_equals: ">=",
  less_than: "<",
  less_than_or_equals: "<=",
  contains: "contient",
  not_contains: "!contient",
  is_empty: "vide",
  is_not_empty: "non vide",
  in: "parmi",
  not_in: "pas parmi",
};

// Canvas dimensions (slide size)
const CANVAS_WIDTH = 960;
const CANVAS_HEIGHT = 540;

// Preset colors
const PRESET_COLORS = [
  "#000000", "#333333", "#666666", "#999999", "#ffffff",
  "#FFCC00", "#00B2A9", "#0066B3", "#003366", "#E31937",
  "#FF6B35", "#7CB518", "#9B5DE5", "#F15BB5", "#00BBF9",
];

// Searchable combobox for field selection (output / input)
function FieldSearchCombobox({
  value,
  onSelect,
  fields,
  placeholder = "Rechercher un champ...",
  emptyMessage = "Aucun champ trouvé",
}: {
  value: string;
  onSelect: (slug: string) => void;
  fields: { slug: string; label: string }[];
  placeholder?: string;
  emptyMessage?: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedField = fields.find((f) => f.slug === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 mt-1 justify-between font-normal overflow-hidden"
        >
          {selectedField ? (
            <span className="truncate text-sm font-mono" title={selectedField.slug}>
              {selectedField.slug}
            </span>
          ) : (
            <span className="text-muted-foreground truncate text-sm">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un slug..." />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {fields.map((field) => (
              <CommandItem
                key={field.slug}
                value={field.slug}
                onSelect={() => {
                  onSelect(field.slug);
                  setOpen(false);
                }}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    "mr-2 h-3 w-3",
                    value === field.slug ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-mono truncate">{field.slug}</span>
                  <span className="text-xs text-muted-foreground truncate">{field.label}</span>
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface PropertiesSidebarProps {
  selectedElement: any | null;
  onUpdate: (properties: Record<string, unknown>) => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  outputFields?: OutputField[];
  formFields?: FormField[];
}

export function PropertiesSidebar({
  selectedElement,
  onUpdate,
  onBringForward,
  onSendBackward,
  onBringToFront,
  onSendToBack,
  onDuplicate,
  onDelete,
  outputFields = [],
  formFields = [],
}: PropertiesSidebarProps) {
  if (!selectedElement) {
    return (
      <div className="w-72 shrink-0 border-l bg-background flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Propriétés</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-sm text-muted-foreground text-center">
            Sélectionnez un élément pour voir ses propriétés
          </p>
        </div>
      </div>
    );
  }

  // Check if multiple elements are selected (ActiveSelection)
  const isMultiSelection = selectedElement.type === "activeselection";
  const selectedCount = isMultiSelection ? selectedElement.getObjects?.()?.length || 0 : 1;

  const isTextElement = !isMultiSelection && (
    selectedElement.type === "textbox" || 
    selectedElement.type === "i-text" || 
    selectedElement.customType === "output_field"
  );

  const isShape = !isMultiSelection && ["rect", "circle", "triangle"].includes(selectedElement.type);
  const isLine = !isMultiSelection && selectedElement.type === "line";
  const isImage = !isMultiSelection && selectedElement.type === "image";
  const isTable = !isMultiSelection && selectedElement.customType === "table";
  const isPlaceholderZone = !isMultiSelection && selectedElement.customType === "placeholder_zone";
  const isOutputValueZone = isPlaceholderZone && selectedElement.placeholderType === "output_value";
  const isInputValueZone = isPlaceholderZone && selectedElement.placeholderType === "input_value";
  const isDataTableZone = isPlaceholderZone && selectedElement.placeholderType === "data_table";

  // Get element type label
  const getElementTypeLabel = () => {
    if (isMultiSelection) return `${selectedCount} éléments sélectionnés`;
    if (selectedElement.customType === "output_field") return "Champ de sortie";
    if (selectedElement.customType === "table") return "Tableau";
    if (selectedElement.customType === "placeholder_zone") {
      if (selectedElement.placeholderType === "output_value") {
        return "Zone valeur output";
      } else if (selectedElement.placeholderType === "input_value") {
        return "Zone valeur input";
      } else if (selectedElement.placeholderType === "data_table") {
        return "Zone tableau de donnees";
      }
      return "Zone grille tarifaire";
    }
    switch (selectedElement.type) {
      case "textbox":
      case "i-text":
        return "Texte";
      case "rect":
        return "Rectangle";
      case "circle":
        return "Cercle";
      case "triangle":
        return "Triangle";
      case "line":
        return "Ligne";
      case "image":
        return "Image";
      default:
        return "Élément";
    }
  };

  return (
    <div className="w-72 shrink-0 border-l bg-background flex flex-col h-full overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="font-semibold">{getElementTypeLabel()}</h3>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 overflow-hidden">
          {/* Multi-selection info */}
          {isMultiSelection && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Les modifications s'appliqueront à tous les éléments sélectionnés.
              </p>
            </div>
          )}

          {/* Position & Size Section - Only for single selection */}
          {!isMultiSelection && (
            <CollapsibleSection title="Position & Taille" defaultOpen>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">X</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.left || 0)}
                      onChange={(e) => onUpdate({ left: parseInt(e.target.value) || 0 })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Y</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.top || 0)}
                      onChange={(e) => onUpdate({ top: parseInt(e.target.value) || 0 })}
                      className="h-8"
                    />
                  </div>
                </div>

                {(selectedElement.width !== undefined || selectedElement.height !== undefined) && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedElement.width !== undefined && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Largeur</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedElement.width * (selectedElement.scaleX || 1))}
                          onChange={(e) => {
                            const newWidth = parseInt(e.target.value) || 100;
                            // For images, we need to adjust scaleX instead of width
                            // because Fabric.js images have fixed original dimensions
                            const isImage = selectedElement.type === "image";
                            if (isImage && selectedElement.width) {
                              const newScaleX = newWidth / selectedElement.width;
                              onUpdate({ scaleX: newScaleX });
                            } else {
                              onUpdate({ width: newWidth, scaleX: 1 });
                            }
                          }}
                          className="h-8"
                        />
                      </div>
                    )}
                    {selectedElement.height !== undefined && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Hauteur</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedElement.height * (selectedElement.scaleY || 1))}
                          onChange={(e) => {
                            const newHeight = parseInt(e.target.value) || 100;
                            // For images, we need to adjust scaleY instead of height
                            // because Fabric.js images have fixed original dimensions
                            const isImage = selectedElement.type === "image";
                            if (isImage && selectedElement.height) {
                              const newScaleY = newHeight / selectedElement.height;
                              onUpdate({ scaleY: newScaleY });
                            } else {
                              onUpdate({ height: newHeight, scaleY: 1 });
                            }
                          }}
                          className="h-8"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Rotation */}
                <div>
                  <Label className="text-xs text-muted-foreground">Rotation</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[selectedElement.angle || 0]}
                      onValueChange={([value]) => onUpdate({ angle: value })}
                      min={0}
                      max={360}
                      step={1}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={Math.round(selectedElement.angle || 0)}
                      onChange={(e) => onUpdate({ angle: parseInt(e.target.value) || 0 })}
                      className="w-16 h-8"
                      min={0}
                      max={360}
                    />
                    <span className="text-xs text-muted-foreground">°</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onUpdate({ angle: 0 })}
                      title="Réinitialiser la rotation"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Centering on slide */}
                <div>
                  <Label className="text-xs text-muted-foreground">Centrer sur le slide</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-8 text-xs gap-1"
                      onClick={() => {
                        const elemWidth = (selectedElement.width || 0) * (selectedElement.scaleX || 1);
                        const originX = selectedElement.originX || "left";
                        const centerX = originX === "center"
                          ? CANVAS_WIDTH / 2
                          : (CANVAS_WIDTH - elemWidth) / 2;
                        onUpdate({ left: centerX });
                      }}
                      title="Centrer horizontalement"
                    >
                      <AlignCenterHorizontal className="h-3.5 w-3.5" />
                      Horizontal
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-8 text-xs gap-1"
                      onClick={() => {
                        const elemHeight = (selectedElement.height || 0) * (selectedElement.scaleY || 1);
                        const originY = selectedElement.originY || "top";
                        const centerY = originY === "center"
                          ? CANVAS_HEIGHT / 2
                          : (CANVAS_HEIGHT - elemHeight) / 2;
                        onUpdate({ top: centerY });
                      }}
                      title="Centrer verticalement"
                    >
                      <AlignCenterVertical className="h-3.5 w-3.5" />
                      Vertical
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => {
                        const elemWidth = (selectedElement.width || 0) * (selectedElement.scaleX || 1);
                        const elemHeight = (selectedElement.height || 0) * (selectedElement.scaleY || 1);
                        const originX = selectedElement.originX || "left";
                        const originY = selectedElement.originY || "top";
                        const centerX = originX === "center"
                          ? CANVAS_WIDTH / 2
                          : (CANVAS_WIDTH - elemWidth) / 2;
                        const centerY = originY === "center"
                          ? CANVAS_HEIGHT / 2
                          : (CANVAS_HEIGHT - elemHeight) / 2;
                        onUpdate({ left: centerX, top: centerY });
                      }}
                      title="Centrer au milieu du slide"
                    >
                      <Maximize className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Text Section - Only for text elements */}
          {isTextElement && (
            <CollapsibleSection title="Texte" defaultOpen>
              <div className="space-y-3">
                {/* Text Content */}
                <div>
                  <Label className="text-xs text-muted-foreground">Contenu</Label>
                  <textarea
                    value={selectedElement.text || ""}
                    onChange={(e) => onUpdate({ text: e.target.value })}
                    className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md resize-y bg-background"
                    placeholder="Entrez votre texte..."
                  />
                </div>

                {/* Font Family */}
                <div>
                  <Label className="text-xs text-muted-foreground">Police</Label>
                  <Select
                    value={selectedElement.fontFamily || "Arial"}
                    onValueChange={(value) => onUpdate({ fontFamily: value })}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_FAMILIES.map((font) => (
                        <SelectItem
                          key={font.value}
                          value={font.value}
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Font Size */}
                <div>
                  <Label className="text-xs text-muted-foreground">Taille</Label>
                  <Input
                    type="number"
                    value={selectedElement.fontSize || 16}
                    onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 16 })}
                    className="h-8"
                    min={8}
                    max={200}
                  />
                </div>

                {/* Text Styles */}
                <div>
                  <Label className="text-xs text-muted-foreground">Style</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.fontWeight === "bold" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ fontWeight: selectedElement.fontWeight === "bold" ? "normal" : "bold" })}
                    >
                      <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.fontStyle === "italic" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ fontStyle: selectedElement.fontStyle === "italic" ? "normal" : "italic" })}
                    >
                      <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.underline && "bg-muted border-primary")}
                      onClick={() => onUpdate({ underline: !selectedElement.underline })}
                    >
                      <Underline className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.linethrough && "bg-muted border-primary")}
                      onClick={() => onUpdate({ linethrough: !selectedElement.linethrough })}
                    >
                      <Strikethrough className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Text Alignment */}
                <div>
                  <Label className="text-xs text-muted-foreground">Alignement</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.textAlign === "left" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ textAlign: "left" })}
                    >
                      <AlignLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.textAlign === "center" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ textAlign: "center" })}
                    >
                      <AlignCenter className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.textAlign === "right" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ textAlign: "right" })}
                    >
                      <AlignRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn("h-8 w-8", selectedElement.textAlign === "justify" && "bg-muted border-primary")}
                      onClick={() => onUpdate({ textAlign: "justify" })}
                    >
                      <AlignJustify className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Line Height */}
                <div>
                  <Label className="text-xs text-muted-foreground">Interligne</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[selectedElement.lineHeight || 1.2]}
                      onValueChange={([value]) => onUpdate({ lineHeight: value })}
                      min={0.5}
                      max={3}
                      step={0.1}
                      className="flex-1"
                    />
                    <span className="text-xs w-8 text-right">
                      {(selectedElement.lineHeight || 1.2).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </span>
                  </div>
                </div>

                {/* Character Spacing */}
                <div>
                  <Label className="text-xs text-muted-foreground">Espacement lettres</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[selectedElement.charSpacing || 0]}
                      onValueChange={([value]) => onUpdate({ charSpacing: value })}
                      min={-100}
                      max={500}
                      step={10}
                      className="flex-1"
                    />
                    <span className="text-xs w-8 text-right">
                      {selectedElement.charSpacing || 0}
                    </span>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Colors Section */}
          <CollapsibleSection title="Couleurs" defaultOpen>
            <div className="space-y-3">
              {/* Fill Color (text color for text elements) */}
              <div>
                <Label className="text-xs text-muted-foreground">
                  {isTextElement ? "Couleur du texte" : "Remplissage"}
                </Label>
                <ColorPicker
                  value={selectedElement.fill || (isTextElement ? "#000000" : "#e5e5e5")}
                  onChange={(color) => onUpdate({ fill: color })}
                />
              </div>

              {/* Background Color - Only for text elements */}
              {isTextElement && (
                <div>
                  <Label className="text-xs text-muted-foreground">Couleur de fond</Label>
                  <ColorPicker
                    value={selectedElement.backgroundColor || "transparent"}
                    onChange={(color) => onUpdate({ backgroundColor: color })}
                    allowTransparent
                  />
                </div>
              )}

              {/* Stroke/Border */}
              {(isShape || isLine || isTextElement) && (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {isLine ? "Couleur" : "Bordure"}
                    </Label>
                    <ColorPicker
                      value={selectedElement.stroke || "#000000"}
                      onChange={(color) => onUpdate({ stroke: color })}
                      allowTransparent={!isLine}
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {isLine ? "Épaisseur" : "Épaisseur bordure"}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[selectedElement.strokeWidth || (isLine ? 2 : 0)]}
                        onValueChange={([value]) => onUpdate({ strokeWidth: value })}
                        min={0}
                        max={20}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs w-8 text-right">
                        {selectedElement.strokeWidth || (isLine ? 2 : 0)}px
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CollapsibleSection>

          {/* Effects Section */}
          <CollapsibleSection title="Effets" defaultOpen>
            <div className="space-y-3">
              {/* Opacity */}
              <div>
                <Label className="text-xs text-muted-foreground">Opacité</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[(selectedElement.opacity ?? 1) * 100]}
                    onValueChange={([value]) => onUpdate({ opacity: value / 100 })}
                    min={0}
                    max={100}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-xs w-10 text-right">
                    {Math.round((selectedElement.opacity ?? 1) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Output Value Zone Configuration */}
          {isOutputValueZone && (
            <CollapsibleSection title="Champ de sortie" defaultOpen>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Champ de calcul à afficher
                  </Label>
                  <FieldSearchCombobox
                    value={selectedElement.outputSlug || ""}
                    onSelect={(value) => {
                      const field = outputFields.find(f => f.slug === value);
                      onUpdate({ 
                        outputSlug: value,
                        placeholderLabel: field?.label || "Valeur"
                      });
                    }}
                    fields={outputFields}
                    placeholder="Rechercher un champ..."
                    emptyMessage="Aucun champ de sortie disponible"
                  />
                </div>

                {selectedElement.outputSlug && (
                  <div className="p-2 bg-muted rounded-md">
                    <p className="text-xs text-muted-foreground">Champ sélectionné :</p>
                    <p className="text-sm font-medium">
                      {outputFields.find(f => f.slug === selectedElement.outputSlug)?.label || selectedElement.outputSlug}
                    </p>
                    <code className="text-xs text-muted-foreground">
                      {selectedElement.outputSlug}
                    </code>
                  </div>
                )}

                {!selectedElement.outputSlug && (
                  <p className="text-xs text-amber-600">
                    Veuillez sélectionner un champ de calcul pour que cette zone affiche une valeur lors de la génération de la proposition.
                  </p>
                )}

                {/* Value Formatting Options */}
                <Separator className="my-3" />
                <div>
                  <Label className="text-xs text-muted-foreground font-medium">
                    Formatage de la valeur
                  </Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Préfixe</Label>
                    <Input
                      type="text"
                      value={selectedElement.prefix || ""}
                      onChange={(e) => onUpdate({ prefix: e.target.value })}
                      placeholder="ex: €"
                      className="h-8 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suffixe</Label>
                    <Input
                      type="text"
                      value={selectedElement.suffix || ""}
                      onChange={(e) => onUpdate({ suffix: e.target.value })}
                      placeholder="ex: EUR"
                      className="h-8 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Décimales</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={selectedElement.decimalPlaces ?? ""}
                    onChange={(e) => {
                      const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                      onUpdate({ decimalPlaces: value });
                    }}
                    placeholder="Auto"
                    className="h-8 mt-1 w-24"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Laissez vide pour garder la valeur d'origine
                  </p>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="thousandSeparatorOutput"
                    checked={selectedElement.useThousandSeparator || false}
                    onCheckedChange={(checked) => onUpdate({ useThousandSeparator: !!checked })}
                  />
                  <Label 
                    htmlFor="thousandSeparatorOutput" 
                    className="text-xs text-muted-foreground cursor-pointer"
                  >
                    Séparateur de milliers (10 000)
                  </Label>
                </div>

                {/* Text Color for Output Value */}
                <Separator className="my-3" />
                <div>
                  <Label className="text-xs text-muted-foreground font-medium">
                    Style du texte
                  </Label>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Couleur du texte</Label>
                  <ColorPicker
                    value={selectedElement.fill || "#000000"}
                    onChange={(color) => onUpdate({ fill: color })}
                  />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Input Value Zone Configuration */}
          {isInputValueZone && (
            <CollapsibleSection title="Champ d'entrée" defaultOpen>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Champ de saisie à afficher
                  </Label>
                  <FieldSearchCombobox
                    value={selectedElement.inputSlug || ""}
                    onSelect={(value) => {
                      const field = formFields.find(f => f.slug === value);
                      onUpdate({ 
                        inputSlug: value,
                        placeholderLabel: field?.label || "Valeur Input"
                      });
                    }}
                    fields={formFields}
                    placeholder="Rechercher un champ..."
                    emptyMessage="Aucun champ de saisie disponible"
                  />
                </div>

                {selectedElement.inputSlug && (
                  <div className="p-2 bg-muted rounded-md">
                    <p className="text-xs text-muted-foreground">Champ sélectionné :</p>
                    <p className="text-sm font-medium">
                      {formFields.find(f => f.slug === selectedElement.inputSlug)?.label || selectedElement.inputSlug}
                    </p>
                    <code className="text-xs text-muted-foreground">
                      {selectedElement.inputSlug}
                    </code>
                  </div>
                )}

                {!selectedElement.inputSlug && (
                  <p className="text-xs text-amber-600">
                    Veuillez sélectionner un champ de saisie pour que cette zone affiche une valeur lors de la génération de la proposition.
                  </p>
                )}

                {/* Value Formatting Options */}
                <Separator className="my-3" />
                <div>
                  <Label className="text-xs text-muted-foreground font-medium">
                    Formatage de la valeur
                  </Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Préfixe</Label>
                    <Input
                      type="text"
                      value={selectedElement.prefix || ""}
                      onChange={(e) => onUpdate({ prefix: e.target.value })}
                      placeholder="ex: €"
                      className="h-8 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suffixe</Label>
                    <Input
                      type="text"
                      value={selectedElement.suffix || ""}
                      onChange={(e) => onUpdate({ suffix: e.target.value })}
                      placeholder="ex: EUR"
                      className="h-8 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Décimales</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={selectedElement.decimalPlaces ?? ""}
                    onChange={(e) => {
                      const value = e.target.value === "" ? undefined : parseInt(e.target.value);
                      onUpdate({ decimalPlaces: value });
                    }}
                    placeholder="Auto"
                    className="h-8 mt-1 w-24"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Laissez vide pour garder la valeur d'origine
                  </p>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="thousandSeparatorInput"
                    checked={selectedElement.useThousandSeparator || false}
                    onCheckedChange={(checked) => onUpdate({ useThousandSeparator: !!checked })}
                  />
                  <Label 
                    htmlFor="thousandSeparatorInput" 
                    className="text-xs text-muted-foreground cursor-pointer"
                  >
                    Séparateur de milliers (10 000)
                  </Label>
                </div>

                {/* Text Color for Input Value */}
                <Separator className="my-3" />
                <div>
                  <Label className="text-xs text-muted-foreground font-medium">
                    Style du texte
                  </Label>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Couleur du texte</Label>
                  <ColorPicker
                    value={selectedElement.fill || "#000000"}
                    onChange={(color) => onUpdate({ fill: color })}
                  />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Data Table Zone Configuration */}
          {isDataTableZone && (
            <DataTableZoneSection
              selectedElement={selectedElement}
              onUpdate={onUpdate}
              outputFields={outputFields}
            />
          )}

          {/* Table Properties Section */}
          {isTable && (
            <TablePropertiesSection
              selectedElement={selectedElement}
              onUpdate={onUpdate}
              outputFields={outputFields}
              formFields={formFields}
            />
          )}

          {/* Organization Section */}
          <CollapsibleSection title="Organisation" defaultOpen>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onBringToFront}
                >
                  <ChevronUp className="h-3 w-3 mr-1" />
                  <ChevronUp className="h-3 w-3 mr-1 -ml-2" />
                  Premier plan
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onSendToBack}
                >
                  <ChevronDown className="h-3 w-3 mr-1" />
                  <ChevronDown className="h-3 w-3 mr-1 -ml-2" />
                  Arrière-plan
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onBringForward}
                >
                  <ChevronUp className="h-3 w-3 mr-1" />
                  Avancer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onSendBackward}
                >
                  <ChevronDown className="h-3 w-3 mr-1" />
                  Reculer
                </Button>
              </div>

              <Separator className="my-2" />

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={onDuplicate}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Dupliquer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          </CollapsibleSection>
        </div>
      </ScrollArea>
    </div>
  );
}

// Data Table Zone Section - configure columns linked to output fields
function DataTableZoneSection({
  selectedElement,
  onUpdate,
  outputFields = [],
}: {
  selectedElement: any;
  onUpdate: (properties: Record<string, unknown>) => void;
  outputFields?: OutputField[];
}) {
  const columns: Array<{ header: string; output_slug: string }> =
    selectedElement.tableColumns || [];

  const addColumn = useCallback(() => {
    const newColumns = [...columns, { header: `Colonne ${columns.length + 1}`, output_slug: "" }];
    onUpdate({ tableColumns: newColumns });
  }, [columns, onUpdate]);

  const removeColumn = useCallback((index: number) => {
    const newColumns = columns.filter((_: unknown, i: number) => i !== index);
    onUpdate({ tableColumns: newColumns });
  }, [columns, onUpdate]);

  const updateColumn = useCallback(
    (index: number, field: "header" | "output_slug", value: string) => {
      const newColumns = columns.map((col: { header: string; output_slug: string }, i: number) =>
        i === index ? { ...col, [field]: value } : col
      );
      onUpdate({ tableColumns: newColumns });
    },
    [columns, onUpdate]
  );

  return (
    <CollapsibleSection title="Colonnes du tableau" defaultOpen>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Definissez les colonnes et associez-les a des champs de calcul. Le tableau sera rempli automatiquement lors de la generation de la proposition.
        </p>

        {columns.map((col: { header: string; output_slug: string }, idx: number) => (
          <div key={idx} className="p-2 border rounded-md space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Colonne {idx + 1}</Label>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive hover:text-destructive"
                onClick={() => removeColumn(idx)}
              >
                <Minus className="h-3 w-3" />
              </Button>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">En-tete</Label>
              <Input
                value={col.header}
                onChange={(e) => updateColumn(idx, "header", e.target.value)}
                className="h-7 text-xs mt-0.5"
                placeholder="Nom de la colonne"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Champ de sortie</Label>
              <FieldSearchCombobox
                value={col.output_slug}
                onSelect={(slug) => updateColumn(idx, "output_slug", slug)}
                fields={outputFields}
                placeholder="Associer un champ..."
                emptyMessage="Aucun champ disponible"
              />
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={addColumn}
        >
          <Plus className="h-3 w-3 mr-1" />
          Ajouter une colonne
        </Button>

        {columns.length === 0 && (
          <p className="text-xs text-amber-600">
            Ajoutez au moins une colonne pour que le tableau soit genere.
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}

// Helper to check if a cell value is a reference
function isCellRef(cell: TableCellValue): cell is TableCellRef {
  return typeof cell === "object" && cell !== null && "slug" in cell;
}

// Helper to get the cell "mode" string
function getCellMode(cell: TableCellValue): "text" | "output" | "input" {
  if (isCellRef(cell)) return cell.type;
  return "text";
}

// Helper to get display text for a cell
function getCellTextValue(cell: TableCellValue): string {
  if (typeof cell === "string") return cell;
  return "";
}

// Compact visibility conditions editor for a table cell reference
function CellVisibilityConditionsEditor({
  cell,
  onChange,
  outputFields = [],
  formFields = [],
}: {
  cell: TableCellRef;
  onChange: (updated: TableCellRef) => void;
  outputFields?: OutputField[];
  formFields?: FormField[];
}) {
  const conditions = cell.visibilityConditions ?? null;
  const hasConditions = conditions !== null && conditions.conditions.length > 0;

  const allFields = [
    ...formFields.map((f) => ({ slug: f.slug, label: `[Entree] ${f.label}` })),
    ...outputFields.map((f) => ({ slug: f.slug, label: `[Sortie] ${f.label}` })),
  ];

  const addCondition = () => {
    const newConditions: VisibilityConditions = conditions
      ? {
          ...conditions,
          conditions: [
            ...conditions.conditions,
            { field_slug: "", operator: "equals" as ConditionOperator, value: "" },
          ],
        }
      : { logic: "AND", conditions: [{ field_slug: "", operator: "equals" as ConditionOperator, value: "" }] };
    onChange({ ...cell, visibilityConditions: newConditions });
  };

  const removeCondition = (index: number) => {
    if (!conditions) return;
    const next = conditions.conditions.filter((_, i) => i !== index);
    onChange({
      ...cell,
      visibilityConditions: next.length === 0 ? undefined : { ...conditions, conditions: next },
    });
  };

  const updateCondition = (index: number, updates: Partial<{ field_slug: string; operator: ConditionOperator; value: unknown }>) => {
    if (!conditions) return;
    const next = [...conditions.conditions];
    next[index] = { ...next[index], ...updates };
    onChange({ ...cell, visibilityConditions: { ...conditions, conditions: next } });
  };

  return (
    <Collapsible defaultOpen={hasConditions} className="group/cond">
      <div className="flex items-center justify-between">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
          <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]/cond:rotate-180" />
          Conditions d'affichage
          {hasConditions && (
            <span className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">
              {conditions!.conditions.length}
            </span>
          )}
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 text-[10px] px-1.5"
          onClick={addCondition}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      <CollapsibleContent>
        {!hasConditions && (
          <div className="mt-1.5">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground border-dashed"
              onClick={addCondition}
            >
              <Plus className="h-3 w-3 mr-1" />
              Ajouter une condition
            </Button>
          </div>
        )}
        {hasConditions && (
          <div className="mt-1.5 space-y-1.5">
            {conditions!.conditions.length > 1 && (
              <Select
                value={conditions!.logic}
                onValueChange={(value: ConditionLogic) =>
                  onChange({ ...cell, visibilityConditions: { ...conditions!, logic: value } })
                }
              >
                <SelectTrigger className="h-6 text-[10px] w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">Toutes (ET)</SelectItem>
                  <SelectItem value="OR">Une (OU)</SelectItem>
                </SelectContent>
              </Select>
            )}

            {conditions!.conditions.map((cond, idx) => (
              <div key={idx} className="flex flex-col gap-1 p-1.5 rounded border border-dashed border-amber-300 bg-amber-50/50">
                <div className="flex items-center gap-1">
                  <FieldSearchCombobox
                    value={cond.field_slug || ""}
                    onSelect={(slug) => updateCondition(idx, { field_slug: slug })}
                    fields={allFields}
                    placeholder="Champ..."
                    emptyMessage="Aucun champ"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeCondition(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <Select
                    value={cond.operator}
                    onValueChange={(value: ConditionOperator) =>
                      updateCondition(idx, { operator: value })
                    }
                  >
                    <SelectTrigger className="h-6 text-[10px] flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(cellConditionOperatorLabels).map(([op, label]) => (
                        <SelectItem key={op} value={op}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!["is_empty", "is_not_empty"].includes(cond.operator) && (
                    <Input
                      value={String(cond.value ?? "")}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      placeholder="Valeur"
                      className="h-6 text-[10px] flex-1"
                    />
                  )}
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              className="w-full h-6 text-[10px] text-muted-foreground border-dashed"
              onClick={addCondition}
            >
              <Plus className="h-3 w-3 mr-1" />
              Ajouter une condition
            </Button>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Table Properties Section
function TablePropertiesSection({
  selectedElement,
  onUpdate,
  outputFields = [],
  formFields = [],
}: {
  selectedElement: any;
  onUpdate: (properties: Record<string, unknown>) => void;
  outputFields?: OutputField[];
  formFields?: FormField[];
}) {
  const rows: number = selectedElement.tableRows || 3;
  const cols: number = selectedElement.tableCols || 3;
  const data: TableCellValue[][] = selectedElement.tableData || [];
  const hasHeader: boolean = selectedElement.tableHasHeader ?? true;
  const hasRowHeader: boolean = selectedElement.tableHasRowHeader ?? false;
  const skipHideZeroRows: boolean = selectedElement.tableSkipHideZeroRows ?? false;
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);

  const updateCellValue = useCallback(
    (rowIdx: number, colIdx: number, value: TableCellValue) => {
      const newData = data.map((row: TableCellValue[], r: number) =>
        row.map((cell: TableCellValue, c: number) =>
          r === rowIdx && c === colIdx ? value : cell
        )
      );
      onUpdate({ tableData: newData });
    },
    [data, onUpdate]
  );

  const insertRowAt = useCallback((index: number) => {
    const newRow: TableCellValue[] = Array.from({ length: cols }, () => "");
    const newData = [...data];
    newData.splice(index, 0, newRow);
    onUpdate({ tableRows: rows + 1, tableData: newData });
  }, [data, rows, cols, onUpdate]);

  const removeRowAt = useCallback((index: number) => {
    if (rows <= 1) return;
    const newData = data.filter((_: TableCellValue[], i: number) => i !== index);
    // Reset editing cell if it was on the deleted row or adjust index
    if (editingCell) {
      if (editingCell.row === index) {
        setEditingCell(null);
      } else if (editingCell.row > index) {
        setEditingCell({ row: editingCell.row - 1, col: editingCell.col });
      }
    }
    onUpdate({ tableRows: rows - 1, tableData: newData });
  }, [data, rows, editingCell, onUpdate]);

  const insertColAt = useCallback((index: number) => {
    const newData = data.map((row: TableCellValue[]) => {
      const newRow = [...row];
      newRow.splice(index, 0, "" as TableCellValue);
      return newRow;
    });
    onUpdate({ tableCols: cols + 1, tableData: newData });
  }, [data, cols, onUpdate]);

  const removeColAt = useCallback((index: number) => {
    if (cols <= 1) return;
    const newData = data.map((row: TableCellValue[]) =>
      row.filter((_: TableCellValue, i: number) => i !== index)
    );
    // Reset editing cell if it was on the deleted column or adjust index
    if (editingCell) {
      if (editingCell.col === index) {
        setEditingCell(null);
      } else if (editingCell.col > index) {
        setEditingCell({ row: editingCell.row, col: editingCell.col - 1 });
      }
    }
    onUpdate({ tableCols: cols - 1, tableData: newData });
  }, [data, cols, editingCell, onUpdate]);

  const currentEditCell = editingCell
    ? data[editingCell.row]?.[editingCell.col]
    : null;
  const currentMode = currentEditCell !== null && currentEditCell !== undefined
    ? getCellMode(currentEditCell)
    : "text";

  return (
    <>
      <CollapsibleSection title="Structure" defaultOpen>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Lignes</Label>
              <div className="flex items-center gap-1 mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={rows <= 1}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {Array.from({ length: rows }, (_, i) => (
                      <DropdownMenuItem
                        key={i}
                        onClick={() => removeRowAt(i)}
                      >
                        <Trash2 className="h-3 w-3 mr-2" />
                        {hasHeader && i === 0 ? "En-tête" : `Ligne ${i + 1}`}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="text-sm w-8 text-center">{rows}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => insertRowAt(0)}>
                      <Plus className="h-3 w-3 mr-2" />
                      Au début
                    </DropdownMenuItem>
                    {Array.from({ length: rows }, (_, i) => (
                      <DropdownMenuItem
                        key={i}
                        onClick={() => insertRowAt(i + 1)}
                      >
                        <Plus className="h-3 w-3 mr-2" />
                        Après {hasHeader && i === 0 ? "l'en-tête" : `la ligne ${i + 1}`}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Colonnes</Label>
              <div className="flex items-center gap-1 mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={cols <= 1}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {Array.from({ length: cols }, (_, i) => (
                      <DropdownMenuItem
                        key={i}
                        onClick={() => removeColAt(i)}
                      >
                        <Trash2 className="h-3 w-3 mr-2" />
                        {hasRowHeader && i === 0 ? "En-tête" : `Colonne ${i + 1}`}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="text-sm w-8 text-center">{cols}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => insertColAt(0)}>
                      <Plus className="h-3 w-3 mr-2" />
                      Au début
                    </DropdownMenuItem>
                    {Array.from({ length: cols }, (_, i) => (
                      <DropdownMenuItem
                        key={i}
                        onClick={() => insertColAt(i + 1)}
                      >
                        <Plus className="h-3 w-3 mr-2" />
                        Après {hasRowHeader && i === 0 ? "l'en-tête" : `la colonne ${i + 1}`}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="tableHasHeader"
              checked={hasHeader}
              onCheckedChange={(checked) => onUpdate({ tableHasHeader: !!checked })}
            />
            <Label
              htmlFor="tableHasHeader"
              className="text-xs text-muted-foreground cursor-pointer"
            >
              Ligne d'en-tete
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="tableHasRowHeader"
              checked={hasRowHeader}
              onCheckedChange={(checked) => onUpdate({ tableHasRowHeader: !!checked })}
            />
            <Label
              htmlFor="tableHasRowHeader"
              className="text-xs text-muted-foreground cursor-pointer"
            >
              Colonne d'en-tete
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="tableSkipHideZeroRows"
              checked={skipHideZeroRows}
              onCheckedChange={(checked) => onUpdate({ tableSkipHideZeroRows: !!checked })}
            />
            <Label
              htmlFor="tableSkipHideZeroRows"
              className="text-xs text-muted-foreground cursor-pointer"
            >
              Toujours afficher (ignorer masquage lignes a zero)
            </Label>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Contenu des cellules" defaultOpen>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {data.map((row: TableCellValue[], rowIdx: number) => (
            <div key={rowIdx}>
              <Label className="text-xs text-muted-foreground">
                {hasHeader && rowIdx === 0 ? "En-tete ligne" : `Ligne ${rowIdx + 1}`}
              </Label>
              <div className="grid gap-1 mt-0.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {row.map((cell: TableCellValue, colIdx: number) => {
                  const mode = getCellMode(cell);
                  const isEditing =
                    editingCell?.row === rowIdx && editingCell?.col === colIdx;
                  const hasVisibilityConditions =
                    isCellRef(cell) &&
                    cell.visibilityConditions &&
                    cell.visibilityConditions.conditions.length > 0;
                  return (
                    <button
                      key={`${rowIdx}-${colIdx}`}
                      className={cn(
                        "h-7 text-xs px-1 border rounded-md text-left truncate min-w-0 overflow-hidden relative",
                        isEditing
                          ? "ring-2 ring-primary border-primary"
                          : "border-input hover:border-primary/50",
                        mode === "output" && "bg-blue-50 text-blue-700 border-blue-200",
                        mode === "input" && "bg-teal-50 text-teal-700 border-teal-200",
                        mode === "text" && "bg-background"
                      )}
                      onClick={() => setEditingCell({ row: rowIdx, col: colIdx })}
                      title={
                        mode === "text"
                          ? getCellTextValue(cell) || `R${rowIdx + 1}C${colIdx + 1}`
                          : `${mode === "output" ? "Output" : "Input"}: ${(cell as TableCellRef).slug}${hasVisibilityConditions ? " (conditionnel)" : ""}`
                      }
                    >
                      {hasVisibilityConditions && (
                        <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-amber-400 border border-amber-600" />
                      )}
                      {mode === "text" ? (
                        <span className="text-muted-foreground truncate block">
                          {getCellTextValue(cell) || `R${rowIdx + 1}C${colIdx + 1}`}
                        </span>
                      ) : (
                        <span className="font-mono truncate block">
                          {mode === "output" ? "↗" : "↙"}{" "}
                          {(cell as TableCellRef).slug || "..."}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Cell detail editor */}
        {editingCell && (
          <div className="mt-3 p-3 border rounded-lg bg-muted/30 space-y-3 overflow-hidden">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">
                Cellule {hasHeader && editingCell.row === 0 ? "en-tete ligne" : `L${editingCell.row + 1}`}
                {hasRowHeader && editingCell.col === 0 ? " en-tete col" : `C${editingCell.col + 1}`}
              </Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => setEditingCell(null)}
              >
                Fermer
              </Button>
            </div>

            {/* Cell type selector */}
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select
                value={currentMode}
                onValueChange={(value: string) => {
                  const { row, col } = editingCell;
                  if (value === "text") {
                    updateCellValue(row, col, "");
                  } else {
                    updateCellValue(row, col, {
                      type: value as "output" | "input",
                      slug: "",
                    });
                  }
                }}
              >
                <SelectTrigger className="h-8 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texte libre</SelectItem>
                  <SelectItem value="output">Valeur output</SelectItem>
                  <SelectItem value="input">Valeur input</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Text input (for text mode) */}
            {currentMode === "text" && (
              <div>
                <Label className="text-xs text-muted-foreground">Contenu</Label>
                <Input
                  value={getCellTextValue(currentEditCell!)}
                  onChange={(e) =>
                    updateCellValue(editingCell.row, editingCell.col, e.target.value)
                  }
                  className="h-8 mt-1"
                  placeholder="Texte..."
                />
              </div>
            )}

            {/* Slug selector (for output/input mode) */}
            {(currentMode === "output" || currentMode === "input") && (
              <>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {currentMode === "output" ? "Champ de sortie" : "Champ d'entree"}
                  </Label>
                  <FieldSearchCombobox
                    value={(currentEditCell as TableCellRef)?.slug || ""}
                    onSelect={(slug) => {
                      const ref = currentEditCell as TableCellRef;
                      updateCellValue(editingCell.row, editingCell.col, {
                        ...ref,
                        slug,
                      });
                    }}
                    fields={currentMode === "output" ? outputFields : formFields}
                    placeholder={
                      currentMode === "output"
                        ? "Rechercher un champ output..."
                        : "Rechercher un champ input..."
                    }
                    emptyMessage="Aucun champ disponible"
                  />
                </div>

                {/* Formatting options */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Prefixe</Label>
                    <Input
                      value={(currentEditCell as TableCellRef)?.prefix || ""}
                      onChange={(e) => {
                        const ref = currentEditCell as TableCellRef;
                        updateCellValue(editingCell.row, editingCell.col, {
                          ...ref,
                          prefix: e.target.value || undefined,
                        });
                      }}
                      className="h-7 text-xs mt-0.5"
                      placeholder="ex: €"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Suffixe</Label>
                    <Input
                      value={(currentEditCell as TableCellRef)?.suffix || ""}
                      onChange={(e) => {
                        const ref = currentEditCell as TableCellRef;
                        updateCellValue(editingCell.row, editingCell.col, {
                          ...ref,
                          suffix: e.target.value || undefined,
                        });
                      }}
                      className="h-7 text-xs mt-0.5"
                      placeholder="ex: EUR"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Decimales</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={(currentEditCell as TableCellRef)?.decimalPlaces ?? ""}
                    onChange={(e) => {
                      const ref = currentEditCell as TableCellRef;
                      const val = e.target.value === "" ? undefined : parseInt(e.target.value);
                      updateCellValue(editingCell.row, editingCell.col, {
                        ...ref,
                        decimalPlaces: val,
                      });
                    }}
                    placeholder="Auto"
                    className="h-7 text-xs mt-0.5 w-20"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cellThousandSep"
                    checked={(currentEditCell as TableCellRef)?.useThousandSeparator || false}
                    onCheckedChange={(checked) => {
                      const ref = currentEditCell as TableCellRef;
                      updateCellValue(editingCell.row, editingCell.col, {
                        ...ref,
                        useThousandSeparator: !!checked,
                      });
                    }}
                  />
                  <Label
                    htmlFor="cellThousandSep"
                    className="text-xs text-muted-foreground cursor-pointer"
                  >
                    Sep. milliers
                  </Label>
                </div>

                {/* Visibility conditions */}
                <CellVisibilityConditionsEditor
                  cell={currentEditCell as TableCellRef}
                  onChange={(updated) =>
                    updateCellValue(editingCell.row, editingCell.col, updated)
                  }
                  outputFields={outputFields}
                  formFields={formFields}
                />
              </>
            )}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Style du tableau" defaultOpen>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Couleur d'en-tete</Label>
            <ColorPicker
              value={selectedElement.tableHeaderBgColor || "#003366"}
              onChange={(color) => onUpdate({ tableHeaderBgColor: color })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Couleur texte en-tete</Label>
            <ColorPicker
              value={selectedElement.tableHeaderTextColor || "#ffffff"}
              onChange={(color) => onUpdate({ tableHeaderTextColor: color })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Couleur bordures</Label>
            <ColorPicker
              value={selectedElement.tableBorderColor || "#dee2e6"}
              onChange={(color) => onUpdate({ tableBorderColor: color })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Couleur du texte</Label>
            <ColorPicker
              value={selectedElement.tableFill || "#000000"}
              onChange={(color) => onUpdate({ tableFill: color })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Taille police</Label>
            <Input
              type="number"
              value={selectedElement.tableFontSize || 12}
              onChange={(e) => onUpdate({ tableFontSize: parseInt(e.target.value) || 12 })}
              className="h-8"
              min={8}
              max={36}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Police</Label>
            <Select
              value={selectedElement.tableFontFamily || "Arial"}
              onValueChange={(value) => onUpdate({ tableFontFamily: value })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_FAMILIES.map((font) => (
                  <SelectItem
                    key={font.value}
                    value={font.value}
                    style={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CollapsibleSection>
    </>
  );
}

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, defaultOpen = true, children }: CollapsibleSectionProps) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="group">
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:text-primary">
        {title}
        <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pt-2 pb-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Color Picker Component
interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  allowTransparent?: boolean;
}

function ColorPicker({ value, onChange, allowTransparent = false }: ColorPickerProps) {
  const isTransparent = value === "transparent" || value === "";

  return (
    <div className="space-y-2 mt-1">
      <div className="grid grid-cols-5 gap-1">
        {allowTransparent && (
          <button
            className={cn(
              "w-6 h-6 rounded border hover:scale-110 transition-transform",
              isTransparent && "ring-2 ring-primary ring-offset-1"
            )}
            style={{
              backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
              backgroundSize: "6px 6px",
              backgroundPosition: "0 0, 0 3px, 3px -3px, -3px 0px",
            }}
            onClick={() => onChange("transparent")}
            title="Transparent"
          />
        )}
        {PRESET_COLORS.slice(0, allowTransparent ? 4 : 5).map((color) => (
          <button
            key={color}
            className={cn(
              "w-6 h-6 rounded border hover:scale-110 transition-transform",
              value === color && "ring-2 ring-primary ring-offset-1"
            )}
            style={{ backgroundColor: color }}
            onClick={() => onChange(color)}
          />
        ))}
      </div>
      <div className="grid grid-cols-5 gap-1">
        {PRESET_COLORS.slice(allowTransparent ? 4 : 5, allowTransparent ? 9 : 10).map((color) => (
          <button
            key={color}
            className={cn(
              "w-6 h-6 rounded border hover:scale-110 transition-transform",
              value === color && "ring-2 ring-primary ring-offset-1"
            )}
            style={{ backgroundColor: color }}
            onClick={() => onChange(color)}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          type="color"
          value={isTransparent ? "#ffffff" : value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 p-0 border-0 cursor-pointer"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-8 text-xs"
          placeholder={allowTransparent ? "transparent" : "#000000"}
        />
      </div>
    </div>
  );
}
